import React, { useState } from "react";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { Popover, PopoverButton, PopoverGroup, PopoverPanel } from "@headlessui/react";

const AssessmentCalendarFilters = ({ sections = [], selectedFilters = {}, onFilterChange }) => {
  const [searchQueries, setSearchQueries] = useState({});

  const toggleOption = (sectionId, optionValue, checked) => {
    const next = { ...selectedFilters };
    const current = next[sectionId] ? [...next[sectionId]] : [];
    if (checked) {
      if (!current.includes(optionValue)) current.push(optionValue);
    } else {
      const index = current.indexOf(optionValue);
      if (index >= 0) current.splice(index, 1);
    }
    if (current.length) next[sectionId] = current;
    else delete next[sectionId];
    onFilterChange(next);
  };

  const activeCount = (sectionId) => selectedFilters[sectionId]?.length || 0;

  const filteredOptions = (section) => {
    const query = (searchQueries[section.id] || "").trim().toLowerCase();
    if (!query) return section.options || [];
    return (section.options || []).filter((option) =>
      String(option.label || "").toLowerCase().includes(query)
    );
  };

  return (
    <section aria-labelledby="calendar-filter-heading">
      <h2 id="calendar-filter-heading" className="sr-only">
        Filters
      </h2>
      <PopoverGroup className="-mx-4 flex flex-wrap items-center divide-x divide-gray-200">
        {sections.map((section) => (
          <Popover key={section.id} className="relative inline-block px-4 text-left">
            <PopoverButton
              className={`group inline-flex justify-center text-sm font-medium ${
                activeCount(section.id) > 0 ? "text-indigo-700" : "text-gray-700"
              } hover:text-gray-900`}
            >
              <span>{section.name}</span>
              {activeCount(section.id) > 0 ? (
                <span className="ml-1.5 rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-indigo-700">
                  {activeCount(section.id)}
                </span>
              ) : null}
              <ChevronDownIcon
                aria-hidden="true"
                className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
              />
            </PopoverButton>
            <PopoverPanel className="absolute right-0 z-30 mt-2 max-h-96 w-80 origin-top-right overflow-y-auto rounded-md bg-white p-4 shadow-2xl ring-1 ring-black/5">
              {section.searchable ? (
                <div className="relative mb-4 text-gray-600">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-1">
                    <MagnifyingGlassIcon aria-hidden="true" className="h-4 w-4" />
                  </div>
                  <input
                    type="search"
                    placeholder="Search"
                    value={searchQueries[section.id] || ""}
                    onChange={(event) =>
                      setSearchQueries((prev) => ({ ...prev, [section.id]: event.target.value }))
                    }
                    className="block w-full rounded-md border bg-white py-1.5 pl-6 pr-3 text-sm leading-6 ring-1 ring-inset ring-gray-300 focus:ring-indigo-300"
                  />
                </div>
              ) : null}
              <div className="space-y-3">
                {filteredOptions(section).map((option) => (
                  <label key={`${section.id}-${option.value}`} className="flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedFilters[section.id]?.includes(option.value) || false}
                      onChange={(event) =>
                        toggleOption(section.id, option.value, event.target.checked)
                      }
                      className="size-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-gray-900">{option.label}</span>
                  </label>
                ))}
              </div>
            </PopoverPanel>
          </Popover>
        ))}
      </PopoverGroup>
    </section>
  );
};

export default AssessmentCalendarFilters;
